import React, { useCallback, useMemo } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  MiniMap,
  useNodesState,
  useEdgesState,
  MarkerType
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Card, CardContent, Typography } from '@mui/material';
import { mockUsers, mockResources, mockConnections } from '../../data/mockData';

const EcosystemGraph: React.FC = () => {
  // Crear nodos para usuarios
  const userNodes: Node[] = useMemo(() => {
    const emprendedores = mockUsers.filter(u => u.role === 'Emprendedor');
    const mentores = mockUsers.filter(u => u.role === 'Mentor');
    
    const emprendedorNodes = emprendedores.map((user, index) => ({
      id: user.id,
      type: 'default',
      data: { label: user.name },
      position: { x: 100, y: 100 + index * 120 },
      style: {
        background: '#2196F3',
        color: 'white',
        border: '2px solid #1976D2',
        borderRadius: '8px',
        padding: '10px',
        fontSize: '12px',
        fontWeight: 'bold'
      }
    }));

    const mentorNodes = mentores.map((user, index) => ({
      id: user.id,
      type: 'default',
      data: { label: `${user.name}\n(${user.expertise})` },
      position: { x: 600, y: 50 + index * 150 },
      style: {
        background: '#4CAF50',
        color: 'white',
        border: '2px solid #388E3C',
        borderRadius: '8px',
        padding: '10px',
        fontSize: '12px',
        fontWeight: 'bold',
        whiteSpace: 'pre-line'
      }
    }));

    return [...emprendedorNodes, ...mentorNodes];
  }, []);

  // Crear nodos para recursos
  const resourceNodes: Node[] = useMemo(() => {
    return mockResources.map((resource, index) => ({
      id: resource.id,
      type: 'default',
      data: { label: `${resource.name}\n(${resource.type})` },
      position: { x: 350, y: 50 + index * 100 },
      style: {
        background: '#9E9E9E',
        color: 'white',
        border: '2px solid #616161',
        borderRadius: '8px',
        padding: '10px',
        fontSize: '11px',
        fontWeight: 'bold',
        whiteSpace: 'pre-line'
      }
    }));
  }, []);

  // Combinar todos los nodos
  const initialNodes: Node[] = useMemo(() => {
    return [...userNodes, ...resourceNodes];
  }, [userNodes, resourceNodes]);

  // Crear edges (conexiones)
  const initialEdges: Edge[] = useMemo(() => {
    return mockConnections.map((conn) => ({
      id: conn.id,
      source: conn.source,
      target: conn.target,
      label: conn.label,
      type: 'smoothstep',
      animated: true,
      style: { stroke: '#666', strokeWidth: 2 },
      labelStyle: { fontSize: 10, fontWeight: 'bold' },
      labelBgStyle: { fill: '#fff', fillOpacity: 0.8 },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: '#666'
      }
    }));
  }, []);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  const onConnect = useCallback(
    (params: any) => setEdges((eds) => [...eds, params]),
    [setEdges]
  );

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Red de Conexiones del Ecosistema
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Visualización interactiva de emprendedores, mentores y recursos
        </Typography>
        <div style={{ height: '600px', border: '1px solid #ddd', borderRadius: '4px' }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            fitView
            attributionPosition="bottom-left"
          >
            <Controls />
            <MiniMap
              nodeColor={(node) => {
                if (node.style?.background) {
                  return node.style.background as string;
                }
                return '#9E9E9E';
              }}
              nodeStrokeWidth={3}
            />
            <Background color="#aaa" gap={16} />
          </ReactFlow>
        </div>
        <Typography variant="caption" display="block" sx={{ mt: 2, color: 'text.secondary' }}>
          <strong>Azul:</strong> Emprendedores | <strong>Verde:</strong> Mentores | <strong>Gris:</strong> Recursos
        </Typography>
      </CardContent>
    </Card>
  );
};

export default EcosystemGraph;
